import { RefObject, useState, useEffect } from "react";
import { useDrop } from "react-dnd";
import { clsx } from "clsx";
import blocksManager from "../../../stores/blocks-manager";
import { IContentBlock } from "../../../types/iblock";
import { StopCircle, MoveVertical } from "lucide-react";

/**
 * At the highest level, what are considered acceptable to drop. In future releases, we will even allow 
 * the dropping of `ImageAssets` directly onto a drop area and immediately create an `ImageBlock` around it.
 */
const ACCEPTABLE_DROP_TYPES = ["DESIGN_BLOCKS", "CONTENT_BLOCKS"];

/**
 * All the acceptable content blocks that can be dropped onto the DesignDropZone.
 */
const CONTENT_BLOCK_TYPES = new Set([
	"COLUMNS",
	"BUTTON",
	"DIVIDER",
	"HEADING",
	"TEXT",
	"IMAGE",
	"HERO_SLIDER",
	"VIDEO",
	"SOCIALS",
	"HTML"
]);

/**
 * Props for the {@link DesignDropZone} component.
 * 
 * @interface IDesignDropZoneProps
 * @description
 * Defines the required and optional properties for the unified `DesignDropZone` component. 
 * This drop zone is used to handle drag-and-drop insertion and reordering of design & content 
 * blocks within both the `DesignCanvas` and `ColumnCell` drop areas. It supports both "between 
 * blocks" and "final (end of list)" drop operations, and is extensible for future enhancements.
 * 
 * @since 2025-07-18
 * @author
 * d2d-weizhi (Chen Weizhi) 
 */
interface IDesignDropZoneProps {
	
	/**
	 * Context is which this drop zone is rendered.
	 * - `"canvas"`: refers to the Top-level `DesignCanvas`.
	 * - `"columns"`: refers to the individual cell within each `ColumnsBlock`
	 */
	context: "canvas" | "columns";

	/**
	 * The `designBlockId` of the block this drop zone is visually paired with. 
	 * Only required for "insert between" mode (not needed for the final drop zone).
	 */
	targetId?: string;

	/**
	 * If true, this drop zone represents the "final" drop area at the end of the block list. 
	 * If false, acts as a "between blocks" drop zone.
	 * Default value: false
	 */
	isFinal: boolean;

	/**
	 * This callback will be fired when a block has been dropped onto it's drop area.
	 * Signature is consistent with all parent containers for maximum reuse.
	 * 
	 * @param isFinal									By default is `false`, but if `true`, then the `handleDrop()` function will deal with it accordingly.
	 * @param blockType 							Either a `CONTENT_BLOCK` (e.g. "TEXT", "IMAGE") or `"DESIGN_BLOCKS"`
	 * @param fromId 									The `designBlockId` of the dragged block (or "" if new)
	 * @param otherColContentBlockId 	(optional) For cross-columns/canvas operations.
	 * @param contentBlockId 					(optional) ID of the underlying content block (for lookups within the `designTree`).
	 * @param contentBlockRef 				(optional) DOM ref for the content block (for toolbars, focus, etc)
	 */
	onDrop: (
		isFinal: boolean,
		blockType: string,
		fromId?: string,
		otherColContentBlockId?: string,
		contentBlockId?: string,
		contentBlockRef?: RefObject<HTMLElement>
	) => void;

}

/**
 * Props for the Design/ContentBlock Payload item.
 * 
 * @interface IBlockPayload
 * @description
 * When a `DesignBlock` or `ContentBlock` is being dropped onto the drop area, it will need to pass a set of 
 * recognized properties/signature. This can include data & information about the `DesignBlock` and `ContentBlock`. 
 * But when a user is dragging a ContentBlock from the Sidebar panel, some of these information won't be required.
 * 
 * @since 2025-07-18
 * @author
 * d2d-weizhi (Chen Weizhi)
 */
interface IBlockPayload {

	/**
	 * Only in used when we're moving a `DesignBlock` across different `ColumnsBlock`.
	 */
	otherColContentBlockId?: string;

	/**
	 * Either a `CONTENT_BLOCK` (e.g. "TEXT", "IMAGE") or `"DESIGN_BLOCKS"`
	 * Also see {@link CONTENT_BLOCK_TYPES}
	 */
  blockType: string;

	/**
	 * References the unique uuid value of the `DesignBlock` being dropped.
	 */
  designBlockId: string;

	/**
	 * The internal ContentBlock's DOM ref. Only provided when moving/reordering a `DesignBlock`.
	 */
  contentBlockRef?: RefObject<HTMLElement>;

	/**
	 * When reordering/moving any `DesignBlock`, the ContentBlock is the main central payload.
	 */
  contentBlock: IContentBlock;
}

export default function DesignDropZone({ 
	context, 
	targetId, 
	isFinal = false, 
	onDrop }: IDesignDropZoneProps
) {
	/** 
	 * We use this state variable to track and monitor when a user is trying to drop a ColumnsBlock inside another
	 * ColumnsBlock. For a version 1.0 release, this operation will be prevented.
	 */
	const [isInvalid, setIsInvalid] = useState(false);

	/**
	 * THREE key things are happening within this `useDrop()` hook:
	 * 1. We are listening for the user's intention of dropping either a `DesignBlock` inside our drop area, or 
	 * 		if the user is about to drop a fresh Content Block selected from the `SidebarPanel`.
	 * 2. When in a hovering state, we also check if the drop operation is a valid one. If it is not allowed, 
	 * 		we will highlight the drop area in red as a visual indicator.
	 * 3. We get the `isOver` state value so that it can help to trigger the DropZone's active state more 
	 * 		responsively.
	 */
	const [{ isOver }, dropRef] = useDrop<IBlockPayload, unknown, { isOver: boolean }>({
    accept: ACCEPTABLE_DROP_TYPES,
    drop: (item: IBlockPayload) => {
      try {
				// We print out the payload data/information of the DesignBlock or ContentBlock that has been dropped here.
        console.log("🧲 DropZone item:", item);

				// Check and validate to see if the block being dropped is either:
				// 1. a ColumnsBlock dragged from the Sidebar panel
				// 2. a DesignBlock that contains a ColumnsBlock.
        const isColumnsBlock = isValidColumnsDrop(item.blockType, item.contentBlock?.blockType);

				// If either of the above conditions are true, we will disallow the user to drop that block here.
        if (context === "columns" && isColumnsBlock) {
          setIsInvalid(true);
          blocksManager.setDropHandled(true);			// <-- Mark as handled. 1st layer check.
          setTimeout(() => {
            setIsInvalid(false);
            blocksManager.setDropHandled(false);	// So that we can continue reordering and dropping other blocks later.
          }, 500); // reset after a short delay
          return { handled: true };              	// <-- Event is consumed, will not bubble
        } else {
					// Okay, user is not going to drop a ColumnsBlock inside another ColumnsBlock, now we can resume normal reordering 
					// operations.
          setIsInvalid(false);

					// If we detect a DesignBlock that is being dropped for reordering
          if (item.blockType === "DESIGN_BLOCKS" && item.designBlockId) {
						// First we inform the global state that a drop is being handled. MobX isn't always the most responsive state
						// management library. After intensive testing, we felt that this is the best place to put this statement.
            blocksManager.setDropHandled(true);
            
						// Now pass the necessary payload information to the actual `handleDrop` function in `DesignCanvas` or `ColumnCell`
            onDrop(
							isFinal,
              item.blockType, 
              item.designBlockId, 
              item.otherColContentBlockId, 
              item.contentBlock?.contentBlockId!, 
              item.contentBlockRef!
            ); // ✅ This gets passed to ColumnCell
            
						// Now we deal with it locally.
						return { handled: true };
          } else if (CONTENT_BLOCK_TYPES.has(item.blockType)) {		// If we detect that a Content Block is being dropped from the Sidebar for reordering
            blocksManager.setDropHandled(true);
            onDrop(
							isFinal,
              item.blockType
            );
            return { handled: true };
          }
        }
        return { handled: false };
      } catch (err) {
        console.log("DesignDropZone drop error: ", err, item);
      }
    },
    hover: (item: IBlockPayload) => {
      const isColumnsBlock = isValidColumnsDrop(item.blockType, item.contentBlock?.blockType);

			// During the hover state, we also want to check if the user is attempting an invalid drop operation.
      if (context === "columns" && isColumnsBlock) {
        setIsInvalid(true);
      } else {
        setIsInvalid(false);
      }

      blocksManager.setHoveredDropZoneId(targetId!);
      blocksManager.setHoveredTarget(targetId!);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

	/**
	 * If the user's cursor is no longer over the drop area, turn off the `isInvalid` state flag.
	 */
	useEffect(() => {
    if (!isOver && isInvalid) {
      setIsInvalid(false);
    }
  }, [isOver, isInvalid]);

	return (
    <div
      ref={dropRef}
      className={clsx(
        "transition-all duration-500 mb-1 rounded-md overflow-hidden mx-[12px]",
        isInvalid
          ? "h-[80px] bg-red-300 border-2 border-red-500"
          : isOver
            ? "h-[80px] bg-blue-200"
            : "h-2 bg-transparent"
      )}
      style={{ minHeight: "8px" }}
    >
      {isInvalid && (
        <span className="text-red-700 text-base flex items-center justify-center h-full">
          <StopCircle size={16} color={"#ffffff"} /> Not allowed
        </span>
      )}
			{isOver && (
				<span className="text-blue-500 text-base flex items-center justify-center h-full">
					<MoveVertical size={24} strokeWidth={1.5} color={"#3b82f6"} /> Drop Here
				</span>
			)}
    </div>
  );
}

/**
 * A tiny internal helper function for validating if the drop operation is allowed.
 * @param payloadBlockType 		Either a `CONTENT_BLOCK` (e.g. "TEXT", "IMAGE") or `"DESIGN_BLOCKS"`
 * @param contentBlockType 		Either a `CONTENT_BLOCK` (e.g. "TEXT", "IMAGE")
 * @returns 									Returns true if the user is dragging a `ColumnsBlock` or a `DesignBlock` that contains one.
 */
function isValidColumnsDrop(payloadBlockType: string, contentBlockType: string): boolean {
	return payloadBlockType.toLowerCase() === "columns" ||	// fresh content block
    (payloadBlockType === "DESIGN_BLOCKS" && contentBlockType.toLowerCase() === "columns")	// DesignBlock
}